// frontend/src/components/CampaignManager/dialogs/CallcenterDialog.tsx
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, FormControl, InputLabel, Select, MenuItem, useMediaQuery, useTheme, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Box, Typography } from '@mui/material';
import { Trash, Plus } from '@phosphor-icons/react';
import { Callcenter, CallcenterPaymentTerm, Lottery } from '../types';
import { API_CONFIG, authFetch } from '../../../config';

interface Props {
  open: boolean;
  callcenter: Callcenter | null;
  onClose: () => void;
  onSave: (callcenter: Partial<Callcenter>, id?: number) => Promise<void>;
}

export default function CallcenterDialog({ open, callcenter, onClose, onSave }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [form, setForm] = useState({ name: '', name_kurz: '', wkz_ziffer: '', active: true });
  const [saving, setSaving] = useState(false);

  // Payment terms state
  const [paymentTerms, setPaymentTerms] = useState<CallcenterPaymentTerm[]>([]);
  const [lotteries, setLotteries] = useState<Lottery[]>([]);
  const [loadingTerms, setLoadingTerms] = useState(false);

  useEffect(() => {
    if (callcenter) {
      setForm({
        name: callcenter.name,
        name_kurz: callcenter.name_kurz,
        wkz_ziffer: callcenter.wkz_ziffer?.toString() || '',
        active: callcenter.active
      });
      loadPaymentTerms(callcenter.id);
    } else {
      setForm({ name: '', name_kurz: '', wkz_ziffer: '', active: true });
      setPaymentTerms([]);
    }
    loadLotteries();
  }, [callcenter, open]);

  const loadLotteries = async () => {
    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/lotteries`);
      const data = await response.json();
      setLotteries(data);
    } catch (error) {
      console.error('Failed to load lotteries:', error);
    }
  };

  const loadPaymentTerms = async (callcenterId: number) => {
    setLoadingTerms(true);
    try {
      const response = await authFetch(`${API_CONFIG.API_V1}/callcenters/${callcenterId}/payment-terms`);
      const data = await response.json();
      setPaymentTerms(data);
    } catch (error) {
      console.error('Failed to load payment terms:', error);
    } finally {
      setLoadingTerms(false);
    }
  };

  const handleAddPaymentTerm = () => {
    setPaymentTerms([
      ...paymentTerms,
      {
        id: -Date.now(), // Temporary negative ID
        callcenter_id: callcenter?.id || 0,
        lottery_id: 0,
        abschlag_prozent: 50,
        abschlag_geld: 0,
        entgelt: 0,
        klasse_abrechnung: 3,
        abrechnungsart: 'EG',
        bemerkungen: ''
      }
    ]);
  };

  const handlePaymentTermChange = (index: number, field: string, value: any) => {
    const updated = [...paymentTerms];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-calculate abschlag_geld when entgelt or abschlag_prozent changes
    if (field === 'entgelt' || field === 'abschlag_prozent') {
      const entgelt = field === 'entgelt' ? value : updated[index].entgelt;
      const abschlagProzent = field === 'abschlag_prozent' ? value : updated[index].abschlag_prozent;
      updated[index].abschlag_geld = (entgelt * abschlagProzent) / 100;
    }

    setPaymentTerms(updated);
  };

  const handleDeletePaymentTerm = async (index: number) => {
    const term = paymentTerms[index];

    if (term.id > 0 && callcenter) {
      // Delete from server
      try {
        await authFetch(`${API_CONFIG.API_V1}/callcenters/${callcenter.id}/payment-terms/${term.id}`, {
          method: 'DELETE'
        });
      } catch (error) {
        console.error('Failed to delete payment term:', error);
        alert('Fehler beim Löschen der Bezahlart');
        return;
      }
    }

    // Remove from local state
    setPaymentTerms(paymentTerms.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save callcenter basic data
      const callcenterData: any = {
        ...form,
        wkz_ziffer: form.wkz_ziffer ? parseInt(form.wkz_ziffer) : undefined
      };

      await onSave(callcenterData, callcenter?.id);

      // Save payment terms if editing existing callcenter
      if (callcenter) {
        for (const term of paymentTerms) {
          if (term.lottery_id === 0) continue; // Skip incomplete terms

          const termData = {
            lottery_id: term.lottery_id,
            abschlag_prozent: term.abschlag_prozent,
            abschlag_geld: term.abschlag_geld,
            entgelt: term.entgelt,
            klasse_abrechnung: term.klasse_abrechnung,
            abrechnungsart: term.abrechnungsart,
            bemerkungen: term.bemerkungen
          };

          if (term.id < 0) {
            // New term - create
            await authFetch(`${API_CONFIG.API_V1}/callcenters/${callcenter.id}/payment-terms`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(termData)
            });
          } else {
            // Existing term - update
            await authFetch(`${API_CONFIG.API_V1}/callcenters/${callcenter.id}/payment-terms/${term.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(termData)
            });
          }
        }
      }

      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>{callcenter ? 'Callcenter bearbeiten' : 'Neues Callcenter'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          {/* Basic Information */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: '#003366', fontSize: '1rem' }}>
              Grunddaten
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Kürzel"
                value={form.name_kurz}
                onChange={(e) => setForm({ ...form, name_kurz: e.target.value })}
                disabled={saving}
                required
                helperText="z.B. CYG"
              />
              <TextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={saving}
                required
                helperText="Name des Callcenters"
              />
              <TextField
                fullWidth
                label="WKZ-Ziffer"
                type="number"
                value={form.wkz_ziffer}
                onChange={(e) => setForm({ ...form, wkz_ziffer: e.target.value })}
                disabled={saving}
                helperText="Die Ziffer in der Los-WKZ (z.B. 2 für '2 6 123')"
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.active ? 'active' : 'inactive'}
                  onChange={(e) => setForm({ ...form, active: e.target.value === 'active' })}
                  label="Status"
                  disabled={saving}
                >
                  <MenuItem value="active">Aktiv</MenuItem>
                  <MenuItem value="inactive">Inaktiv</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {/* Payment Terms */}
          {callcenter && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#003366', fontSize: '1rem' }}>
                  Bezahlarten
                </Typography>
                <Button
                  startIcon={<Plus size={20} />}
                  onClick={handleAddPaymentTerm}
                  size="small"
                  variant="outlined"
                  disabled={saving || loadingTerms}
                >
                  Hinzufügen
                </Button>
              </Box>

              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Lotterie</TableCell>
                      <TableCell>Abschlag %</TableCell>
                      <TableCell>Abschlag €</TableCell>
                      <TableCell>Entgelt €</TableCell>
                      <TableCell>Klasse</TableCell>
                      <TableCell>Art</TableCell>
                      <TableCell>Bemerkungen</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentTerms.map((term, index) => (
                      <TableRow key={term.id}>
                        <TableCell>
                          <Select
                            size="small"
                            value={term.lottery_id}
                            onChange={(e) => handlePaymentTermChange(index, 'lottery_id', Number(e.target.value))}
                            disabled={saving}
                            fullWidth
                          >
                            <MenuItem value={0}>Wählen...</MenuItem>
                            {lotteries.map(lottery => (
                              <MenuItem key={lottery.id} value={lottery.id}>
                                {lottery.name_kurz} {lottery.year}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={term.abschlag_prozent}
                            onChange={(e) => handlePaymentTermChange(index, 'abschlag_prozent', Number(e.target.value))}
                            disabled={saving}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={term.abschlag_geld.toFixed(2)}
                            disabled
                            sx={{ width: 100, '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#000' } }}
                            title="Wird automatisch berechnet: Entgelt × Abschlag%"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={term.entgelt}
                            onChange={(e) => handlePaymentTermChange(index, 'entgelt', Number(e.target.value))}
                            disabled={saving}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={term.klasse_abrechnung}
                            onChange={(e) => handlePaymentTermChange(index, 'klasse_abrechnung', Number(e.target.value))}
                            disabled={saving}
                            sx={{ width: 60 }}
                          >
                            <MenuItem value={1}>1</MenuItem>
                            <MenuItem value={3}>3</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={term.abrechnungsart}
                            onChange={(e) => handlePaymentTermChange(index, 'abrechnungsart', e.target.value)}
                            disabled={saving}
                            sx={{ width: 80 }}
                          >
                            <MenuItem value="EG">EG</MenuItem>
                            <MenuItem value="IN">IN</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={term.bemerkungen || ''}
                            onChange={(e) => handlePaymentTermChange(index, 'bemerkungen', e.target.value)}
                            disabled={saving}
                            placeholder="Bemerkungen..."
                            sx={{ width: 150 }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleDeletePaymentTerm(index)}
                            disabled={saving}
                            color="error"
                          >
                            <Trash size={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {paymentTerms.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3, color: '#6b7280' }}>
                          Keine Bezahlarten definiert. Klicken Sie auf "Hinzufügen", um eine zu erstellen.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Abbrechen</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{ backgroundColor: '#003366' }}
          disabled={saving || !form.name || !form.name_kurz}
        >
          {saving ? 'Speichert...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
